import { X, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface RewardsWalletModalsProps {
  // Claim Modal
  showClaimModal: boolean;
  onCloseClaim: () => void;
  claimableRewards: number;
  isClaiming?: boolean;
  onConfirmClaim: () => void;

  // Send Modal
  showSendModal: boolean;
  onCloseSend: () => void;
  sendAmount: string;
  setSendAmount: (amount: string) => void;
  sendRecipient: string;
  setSendRecipient: (recipient: string) => void;
  availableBalance: number;
  onConfirmSend: () => void;

  // Transfer Modal
  showTransferModal: boolean;
  onCloseTransfer: () => void;
  transferAmount: string;
  setTransferAmount: (amount: string) => void;
  selectedWalletType: 'custodial' | 'external';
  setSelectedWalletType: (type: 'custodial' | 'external') => void;
  externalWalletAddress: string;
  setExternalWalletAddress: (address: string) => void;
  isTransferring?: boolean;
  onConfirmTransfer: () => void;

  // Success Screen
  showSuccessScreen: 'claim' | 'send' | 'transfer' | null;
  onCloseSuccess: () => void;
}

export function RewardsWalletModals({
  showClaimModal,
  onCloseClaim,
  claimableRewards,
  isClaiming,
  onConfirmClaim,
  showSendModal,
  onCloseSend,
  sendAmount,
  setSendAmount,
  sendRecipient,
  setSendRecipient,
  availableBalance,
  onConfirmSend,
  showTransferModal,
  onCloseTransfer,
  transferAmount,
  setTransferAmount,
  selectedWalletType,
  setSelectedWalletType,
  externalWalletAddress,
  setExternalWalletAddress,
  isTransferring,
  onConfirmTransfer,
  showSuccessScreen,
  onCloseSuccess,
}: RewardsWalletModalsProps) {
  const { t } = useTranslation();
  return (
    <>
      {/* Claim Rewards Modal */}
      {showClaimModal && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={onCloseClaim}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#E2DFEA] dark:border-white/8">
              <h2
                className="text-2xl text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.walletModals.claimRewards')}
              </h2>
              <button
                onClick={onCloseClaim}
                className="hover:bg-[#F0EFF5] dark:hover:bg-white/10 p-2 rounded-lg transition-colors text-[#464452] dark:text-white/60"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Amount Display */}
              <div className="text-center p-8 rounded-2xl mb-6 bg-[#F0EFF5] dark:bg-purple-900/10">
                <div
                  className="text-sm mb-2 text-[#464452] dark:text-white/60"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.availableToClaim')}
                </div>
                <div className="flex items-baseline justify-center gap-2">
                  <span
                    className="text-5xl text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {claimableRewards}
                  </span>
                  <span
                    className="text-2xl text-[#936FEF] dark:text-purple-300"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    PETS
                  </span>
                </div>
                <div
                  className="text-sm mt-2 text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.fromReferralsBookings')}
                </div>
              </div>

              {/* Network Fee Info */}
              <div className="flex items-center justify-between p-4 rounded-xl mb-6 bg-[#F0EFF5] dark:bg-green-900/10">
                <span
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.networkFee')}
                </span>
                <span
                  className="text-[#10B981] dark:text-green-300"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.walletModals.sponsored')}
                </span>
              </div>

              {/* Action Buttons */}
              <button
                onClick={onConfirmClaim}
                disabled={isClaiming}
                className="w-full px-6 py-4 rounded-xl text-white hover:opacity-90 transition-opacity mb-3 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background:
                    'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {isClaiming ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('rewards.walletModals.claiming')}
                  </>
                ) : (
                  t('rewards.walletModals.confirmAndClaim')
                )}
              </button>
              <button
                onClick={onCloseClaim}
                className="w-full px-6 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.walletModals.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Rewards Modal */}
      {showSendModal && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={onCloseSend}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#E2DFEA] dark:border-white/8">
              <h2
                className="text-2xl text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.walletModals.sendRewards')}
              </h2>
              <button
                onClick={onCloseSend}
                className="hover:bg-[#F0EFF5] dark:hover:bg-white/10 p-2 rounded-lg transition-colors text-[#464452] dark:text-white/60"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Available Balance */}
              <div className="text-center p-4 rounded-xl mb-6 bg-[#F0EFF5] dark:bg-purple-900/10">
                <div
                  className="text-sm text-[#464452] dark:text-white/60"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.availableBalance')}
                </div>
                <div className="flex items-baseline justify-center gap-2">
                  <span
                    className="text-2xl text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {availableBalance}
                  </span>
                  <span
                    className="text-[#936FEF] dark:text-purple-300"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    PETS
                  </span>
                </div>
              </div>

              {/* Recipient Input */}
              <div className="mb-4">
                <label
                  className="block mb-2 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t('rewards.walletModals.sendTo')}
                </label>
                <select
                  value={sendRecipient}
                  onChange={(e) => setSendRecipient(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl outline-none focus:ring-2 border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white bg-white dark:bg-[#0F0E16]"
                  style={
                    {
                      fontFamily: 'Inter, sans-serif',
                      '--tw-ring-color': '#936FEF',
                    } as React.CSSProperties
                  }
                >
                  <option value="">
                    {t('rewards.walletModals.selectRecipient')}
                  </option>
                  <option value="username">
                    {t('rewards.walletModals.optionUsername')}
                  </option>
                  <option value="did">
                    {t('rewards.walletModals.optionDid')}
                  </option>
                  <option value="wallet">
                    {t('rewards.walletModals.optionWallet')}
                  </option>
                  <option value="qr">
                    {t('rewards.walletModals.optionQr')}
                  </option>
                </select>
              </div>

              {sendRecipient && (
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder={
                      sendRecipient === 'username'
                        ? t('rewards.walletModals.optionUsername')
                        : sendRecipient === 'did'
                          ? t('rewards.walletModals.optionDid')
                          : sendRecipient === 'wallet'
                            ? t('rewards.walletModals.optionWallet')
                            : t('rewards.walletModals.selectRecipient')
                    }
                    className="w-full px-4 py-3 rounded-xl outline-none focus:ring-2 border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white bg-white dark:bg-[#0F0E16] placeholder:text-[#464452]/50 dark:placeholder:text-white/40"
                    style={
                      {
                        fontFamily: 'Inter, sans-serif',
                        '--tw-ring-color': '#936FEF',
                      } as React.CSSProperties
                    }
                  />
                </div>
              )}

              {/* Amount Input */}
              <div className="mb-4">
                <label
                  className="block mb-2 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t('rewards.walletModals.amount')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={sendAmount}
                    onChange={(e) => setSendAmount(e.target.value)}
                    placeholder="0"
                    className="w-full px-4 py-3 rounded-xl outline-none focus:ring-2 pr-24 border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white bg-white dark:bg-[#0F0E16]"
                    style={
                      {
                        fontFamily: 'Inter, sans-serif',
                        '--tw-ring-color': '#936FEF',
                      } as React.CSSProperties
                    }
                  />
                  <div
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#936FEF] dark:text-purple-300"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    PETS
                  </div>
                </div>
                <button
                  onClick={() => setSendAmount(availableBalance.toString())}
                  className="text-sm mt-2 hover:underline text-[#936FEF] dark:text-purple-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.useMax')}
                </button>
              </div>

              {/* Optional Note */}
              <div className="mb-6">
                <label
                  className="block mb-2 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t('rewards.walletModals.noteOptional')}
                </label>
                <textarea
                  placeholder={t('rewards.walletModals.addMessage')}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl outline-none focus:ring-2 resize-none border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white bg-white dark:bg-[#0F0E16] placeholder:text-[#464452]/50 dark:placeholder:text-white/40"
                  style={
                    {
                      fontFamily: 'Inter, sans-serif',
                      '--tw-ring-color': '#936FEF',
                    } as React.CSSProperties
                  }
                />
              </div>

              {/* Network Fee Info */}
              <div className="flex items-center justify-between p-4 rounded-xl mb-6 bg-[#F0EFF5] dark:bg-green-900/10">
                <span
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.networkFee')}
                </span>
                <span
                  className="text-[#10B981] dark:text-green-300"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.walletModals.sponsored')}
                </span>
              </div>

              {/* Action Buttons */}
              <button
                onClick={onConfirmSend}
                disabled={!sendAmount || !sendRecipient}
                className="w-full px-6 py-4 rounded-xl text-white hover:opacity-90 transition-opacity mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background:
                    'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {t('rewards.walletModals.sendRewards')}
              </button>
              <button
                onClick={onCloseSend}
                className="w-full px-6 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.walletModals.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer to Wallet Modal */}
      {showTransferModal && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={onCloseTransfer}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#E2DFEA] dark:border-white/8">
              <h2
                className="text-2xl text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.walletModals.transferToWallet')}
              </h2>
              <button
                onClick={onCloseTransfer}
                className="hover:bg-[#F0EFF5] dark:hover:bg-white/10 p-2 rounded-lg transition-colors text-[#464452] dark:text-white/60"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Wallet Type Selection */}
              <div className="mb-6">
                <label
                  className="block mb-3 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t('rewards.walletModals.selectWalletType')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSelectedWalletType('custodial')}
                    className={`p-4 rounded-xl transition-all ${selectedWalletType === 'custodial' ? 'border-2 border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20' : 'border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16]'}`}
                  >
                    <div className="text-2xl mb-2">🔐</div>
                    <div
                      className="text-sm text-[#2D2F7E] dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('rewards.walletModals.petsoWallet')}
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedWalletType('external')}
                    className={`p-4 rounded-xl transition-all ${selectedWalletType === 'external' ? 'border-2 border-[#936FEF] bg-[#F0EFF5] dark:bg-purple-900/20' : 'border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16]'}`}
                  >
                    <div className="text-2xl mb-2">🔗</div>
                    <div
                      className="text-sm text-[#2D2F7E] dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('rewards.walletModals.externalWallet')}
                    </div>
                  </button>
                </div>
              </div>

              {/* External Wallet Address */}
              {selectedWalletType === 'external' && (
                <div className="mb-4">
                  <label
                    className="block mb-2 text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    {t('rewards.walletModals.walletAddress')}
                  </label>
                  <input
                    type="text"
                    value={externalWalletAddress}
                    onChange={(e) => setExternalWalletAddress(e.target.value)}
                    placeholder={t(
                      'rewards.walletModals.enterSolanaPlaceholder'
                    )}
                    className="w-full px-4 py-3 rounded-xl outline-none focus:ring-2 border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white bg-white dark:bg-[#0F0E16] placeholder:text-[#464452]/50 dark:placeholder:text-white/40"
                    style={
                      {
                        fontFamily: 'Inter, sans-serif',
                        '--tw-ring-color': '#936FEF',
                      } as React.CSSProperties
                    }
                  />
                  <div
                    className="text-xs mt-1 text-[#464452] dark:text-white/60"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.walletModals.networkSolana')}
                  </div>
                </div>
              )}

              {/* Amount Input */}
              <div className="mb-6">
                <label
                  className="block mb-2 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {t('rewards.walletModals.amount')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    placeholder="0"
                    className="w-full px-4 py-3 rounded-xl outline-none focus:ring-2 pr-24 border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white bg-white dark:bg-[#0F0E16]"
                    style={
                      {
                        fontFamily: 'Inter, sans-serif',
                        '--tw-ring-color': '#936FEF',
                      } as React.CSSProperties
                    }
                  />
                  <div
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#936FEF] dark:text-purple-300"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    PETS
                  </div>
                </div>
                <button
                  onClick={() => setTransferAmount(availableBalance.toString())}
                  className="text-sm mt-2 hover:underline text-[#936FEF] dark:text-purple-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.transferAll')}
                </button>
              </div>

              {/* Network Fee Info */}
              <div className="flex items-center justify-between p-4 rounded-xl mb-6 bg-[#F0EFF5] dark:bg-green-900/10">
                <span
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('rewards.walletModals.networkFee')}
                </span>
                <span
                  className="text-[#10B981] dark:text-green-300"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.walletModals.sponsored')}
                </span>
              </div>

              {/* Action Buttons */}
              <button
                onClick={onConfirmTransfer}
                disabled={
                  isTransferring ||
                  !transferAmount ||
                  (selectedWalletType === 'external' && !externalWalletAddress)
                }
                className="w-full px-6 py-4 rounded-xl text-white hover:opacity-90 transition-opacity mb-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background:
                    'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {isTransferring ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('rewards.walletModals.transferring')}
                  </>
                ) : (
                  t('rewards.walletModals.transferNow')
                )}
              </button>
              <button
                onClick={onCloseTransfer}
                className="w-full px-6 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.walletModals.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Screen */}
      {showSuccessScreen && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={onCloseSuccess}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Content */}
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center text-4xl bg-[#10B981]">
                <Check className="w-12 h-12 text-white" />
              </div>

              {/* Success Message */}
              <h2
                className="text-2xl mb-4 text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {showSuccessScreen === 'claim' &&
                  `🎉 ${t('rewards.walletModals.rewardsClaimed')}`}
                {showSuccessScreen === 'send' &&
                  `✅ ${t('rewards.walletModals.transferComplete')}`}
                {showSuccessScreen === 'transfer' &&
                  `🔐 ${t('rewards.walletModals.fundsTransferred')}`}
              </h2>

              <p
                className="mb-8 text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {showSuccessScreen === 'claim' &&
                  t('rewards.walletModals.rewardsClaimedDesc')}
                {showSuccessScreen === 'send' &&
                  t('rewards.walletModals.rewardsSentDesc')}
                {showSuccessScreen === 'transfer' &&
                  t('rewards.walletModals.fundsTransferredDesc')}
              </p>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={onCloseSuccess}
                  className="w-full px-6 py-4 rounded-xl text-white hover:opacity-90 transition-opacity"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {t('rewards.walletModals.viewBalance')}
                </button>
                {showSuccessScreen === 'claim' && (
                  <>
                    <button
                      onClick={() => {
                        onCloseSuccess();
                        toast.info(t('rewards.walletModals.openingSendFlow'));
                      }}
                      className="w-full px-6 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {t('rewards.walletModals.sendRewardsBtn')}
                    </button>
                    <button
                      onClick={() => {
                        onCloseSuccess();
                        toast.info(
                          t('rewards.walletModals.openingTransferFlow')
                        );
                      }}
                      className="w-full px-6 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {t('rewards.walletModals.transferToWalletBtn')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
